import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";

const PORTAL_ROLES = ["aluno", "responsavel"];

const PROFILE_LABELS = {
  aluno: "Portal do Aluno",
  professor: "Área do Professor",
  administrativo: "Administrativo",
};

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const perfilLabel = PROFILE_LABELS[searchParams.get("perfil")];
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await login(email, password);
      navigate(PORTAL_ROLES.includes(data.role) ? "/portal" : "/admin");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async () => {
    if (!email) {
      toast.info("Digite seu e-mail no campo acima e clique novamente.");
      return;
    }
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("Se o e-mail existir, as instruções de recuperação serão enviadas.");
    } catch {
      toast.success("Se o e-mail existir, as instruções de recuperação serão enviadas.");
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img
          src="https://images.pexels.com/photos/37824440/pexels-photo-37824440.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Instituto Musical Jacaré"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/80" />
        <div className="relative flex h-full flex-col justify-between p-12">
          <Logo dark />
          <div>
            <h1 className="font-heading text-4xl font-extrabold leading-tight text-white">
              Instituto Musical Jacaré
            </h1>
            <p className="mt-3 max-w-md text-base text-zinc-300">
              20 anos formando músicos em Cabreúva — SP. Gestão de cursos, professores, horários e salas em um só lugar.
            </p>
            <div className="mt-6 h-1 w-24 bg-primary" />
          </div>
          <p className="text-xs text-zinc-500">Rua Luiz Nunes, 36 — Bairro Jacaré, Cabreúva/SP</p>
        </div>
      </div>

      <div className="flex items-center justify-center bg-white p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <Logo />
          </div>
          {perfilLabel && (
            <div className="mb-4 flex items-center justify-between border border-border bg-zinc-50 px-3 py-2" data-testid="login-profile-badge">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Perfil: <span className="text-foreground">{perfilLabel}</span>
              </span>
              <Link to="/acesso" className="text-xs font-medium text-primary underline-offset-2 transition-colors hover:underline" data-testid="login-change-profile">
                Trocar perfil
              </Link>
            </div>
          )}
          <h2 className="font-heading text-3xl font-bold" data-testid="login-title">Acesso ao painel</h2>
          <p className="mt-1 text-sm text-muted-foreground">Entre com seu e-mail e senha.</p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="login-form">
            {error && (
              <div className="flex items-start gap-2 border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive" data-testid="login-error-alert">
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Senha</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-primary font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
              data-testid="login-submit-button"
            >
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Entrar
            </Button>
            <button
              type="button"
              onClick={handleForgot}
              className="w-full text-center text-xs text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline"
              data-testid="forgot-password-button"
            >
              Esqueci minha senha
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
